import type { HTMLAttributes } from 'react';

type Tone = 'default' | 'ippc' | 'wood' | 'danger';
type Size = 'sm' | 'md';

interface CodeProps extends HTMLAttributes<HTMLSpanElement> {
  /** 编码内容，如 "SL-PLT-2026-0187" */
  children: React.ReactNode;
  /** 语义色调：合规号走 ippc，作业号走 wood，异常号走 danger */
  tone?: Tone;
  size?: Size;
}

const toneClasses: Record<Tone, string> = {
  default: 'bg-pine-100 text-walnut-900',
  wood:    'bg-wood-100 text-wood-700',
  ippc:    'bg-ippc-100 text-ippc-700',
  danger:  'bg-danger-soft text-danger',
};

const sizeClasses: Record<Size, string> = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-0.5',
};

/**
 * 签名动作 B · 条码化单号
 *
 * 用于订单号、工单号、物料编码、BOM 号等所有"标识符"类数据。
 * 等宽字体 + 字距展开 + 牛皮纸底色，视觉上是"条码下方数字"，
 * 一眼区分于描述性文字。详见 DESIGN.md §9.2。
 *
 * @example
 *   <Code>SL-PLT-2026-0187</Code>
 *   <Code tone="ippc">IPPC-CN31-HT-0412</Code>
 *   <Code tone="danger">OVERDUE-9</Code>
 */
export default function Code({ children, tone = 'default', size = 'md', className = '', ...rest }: CodeProps) {
  return (
    <span
      {...rest}
      className={`inline-block rounded-sm font-mono tabular-nums ${toneClasses[tone]} ${sizeClasses[size]} ${className}`}
      style={{ letterSpacing: '0.04em' }}
    >
      {children}
    </span>
  );
}
